import { ref, computed, type Ref } from 'vue';
import type { Praeparat } from '@/types/otc/praeparat';
import type { OtcKategorie } from '@/types/otc/otcKategorie';
import type { SearchType } from './useProductOverviewUI';

/**
 * Verwaltet die Kategorienlogik für die Listenansicht
 * Zuständig für Kategorien-Gruppierung, Auf-/Zuklappen und Produktzuordnung
 */
export function useProductOverviewCategories(
    allProducts: Ref<Praeparat[]>,
    categories: Ref<OtcKategorie[]>,
    searchType: Ref<SearchType>
) {
    const openCategories = ref<Set<number>>(new Set());

    /**
     * Erstellt Kategorien mit zugeordneten Produkten für die Listenansicht
     */
    const categoriesWithProducts = computed(() => {
        if (searchType.value !== 'liste') return [];
        const categoriesList = categories.value.map(category => {
            const categoryProducts = allProducts.value.filter(product =>
                product.otcKategorieId === category.id.toString()
            );
            return {
                ...category,
                Results: categoryProducts.map(product => ({
                    Source: product
                }))
            };
        });
        const returnList = categoriesList.filter(category => category.Results.length > 0);
        return returnList
    });

    /**
     * Wechselt den Auf-/Zugeklappt-Zustand einer Kategorie
     */
    const toggleCategory = (categoryId: any) => {
        if (openCategories.value.has(categoryId)) {
            openCategories.value.delete(categoryId);
        } else {
            openCategories.value.add(categoryId);
        }
    };

    /**
     * Prüft ob eine Kategorie aufgeklappt ist
     */
    const isCategoryOpen = (categoryId: number) => {
        return openCategories.value.has(categoryId);
    };

    return {
        openCategories,
        categoriesWithProducts,
        toggleCategory,
        isCategoryOpen
    };
}